import { useRef, useEffect } from 'react';
import { createChart, AreaSeries, type IChartApi, type ISeriesApi, ColorType, LineType, CrosshairMode, type UTCTimestamp } from 'lightweight-charts';
import { useTheme } from '@/lib/theme';
import { getChartColors } from '@/lib/chart-colors';

interface EquityCurveProps {
  data: { time: number; equity: number }[];
  initialCapital: number;
  height?: number;
}

export function EquityCurve({ data, initialCapital, height = 160 }: EquityCurveProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Area'> | null>(null);
  const { theme } = useTheme();
  const colors = getChartColors(theme);

  // Create chart once on mount
  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: colors.textColor,
        fontSize: 9,
      },
      grid: {
        vertLines: { color: colors.gridColor },
        horzLines: { color: colors.gridColor },
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { color: colors.crosshairColor, width: 1, style: 2, labelBackgroundColor: colors.crosshairLabelBg },
        horzLine: { color: colors.crosshairColor, width: 1, style: 2, labelBackgroundColor: colors.crosshairLabelBg },
      },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: { top: 0.1, bottom: 0.05 },
      },
      timeScale: {
        visible: false,
      },
      handleScroll: false,
      handleScale: false,
    });

    const areaSeries = chart.addSeries(AreaSeries, {
      lineWidth: 2,
      lineType: LineType.Curved,
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 3,
    });

    chartRef.current = chart;
    seriesRef.current = areaSeries;

    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, [height, theme]);

  // Update data + colors without recreating the chart
  useEffect(() => {
    const series = seriesRef.current;
    const chart = chartRef.current;
    if (!series || !chart || data.length < 2) return;

    const finalEquity = data[data.length - 1]!.equity;
    const isProfit = finalEquity >= initialCapital;

    const lineColor = isProfit ? colors.profitLineColor : colors.lossLineColor;
    const topColor = isProfit ? colors.profitTopColor : colors.lossTopColor;
    const bottomColor = isProfit ? colors.profitBottomColor : colors.lossBottomColor;

    series.applyOptions({
      lineColor,
      topColor,
      bottomColor,
      crosshairMarkerBackgroundColor: lineColor,
    });

    // Deduplicate same-timestamp points (e.g. CLOSE_ALL closes multiple trades
    // at the same second). Keep only the last value per timestamp so the chart
    // draws a clean line instead of branching.
    const deduped = new Map<number, number>();
    for (const d of data) {
      const val = d.equity;
      if (val == null || !isFinite(val)) continue; // skip null/undefined/NaN
      const ts = Math.floor(d.time / 1000);
      deduped.set(ts, val);
    }
    const chartData = Array.from(deduped.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([ts, equity]) => ({ time: ts as UTCTimestamp, value: equity }));

    series.setData(chartData);

    requestAnimationFrame(() => {
      chart.timeScale().setVisibleLogicalRange({ from: -0.5, to: chartData.length - 0.5 });
    });
  }, [data, initialCapital, colors]);

  if (data.length < 2) return null;

  return (
    <div className="w-full rounded-md overflow-hidden">
      <div ref={containerRef} className="w-full" style={{ height }} />
    </div>
  );
}
